import React, { FC, useCallback, useMemo, useState } from 'react';

// components
import BasicButton from '../../../../../components/buttons/basicButton';
import BasicModal from '../../../../../components/modals/basicModal';
import styled from 'styled-components';

// constants
import aampeTheme from '../../../../../constants/aampeTheme';

// helper
import { mapAlternateTextToAlternateUpdateModel } from '../../../../../helpers/alternateHelper';
import { getUniqueElementsByKey } from '../../../../../helpers/arrayHelper';

// models
import {
    ImageAlternateModalId,
    VariantUpdateModel,
} from '../../../../../models/alternate.model';
import { VariantWithLabel } from '../../../../../models/variant.model';

import ImageAlternateBody from './ImageAlternateBody';

interface ImageAlternateModalProps {
    handleClose: () => void;
    handleAddAlternates: (data: VariantUpdateModel[]) => void;
    data: VariantWithLabel[];
    formulaId: number;
    isLoading?: boolean;
    isExisting?: boolean;
    handleDeleteMaster: () => void;
    isEditable: boolean;
}

const ImageAlternateModal: FC<
    React.PropsWithChildren<ImageAlternateModalProps>
> = ({
    handleClose,
    handleAddAlternates,
    data,
    formulaId,
    isLoading = false,
    handleDeleteMaster,
    isExisting = false,
    isEditable,
}) => {
    const [showDeleteConfirmation, setShowDeleteConfirmation] =
        useState<boolean>(false);
    const getBlankAlternate: (id: string, is_master: boolean) => any =
        useCallback(
            (id, is_master) => ({
                id,
                text: '',
                is_master,
                deleted: false,
            }),
            [],
        );

    const getInitialData = useCallback<
        (data: VariantWithLabel[]) => VariantUpdateModel[]
    >(
        (initialData: VariantWithLabel[]) => {
            if (initialData.length) {
                return mapAlternateTextToAlternateUpdateModel(initialData);
            }
            return [getBlankAlternate(`ALTERNATE_`, true)];
        },
        [getBlankAlternate],
    );

    const initialData = getInitialData(data);

    const [alternates, setAlternates] =
        useState<VariantUpdateModel[]>(initialData);

    const handleAdd = () => {
        const uniqueAlternates = getUniqueElementsByKey(alternates, 'text');
        const data = uniqueAlternates
            .filter((item) => !!item.text)
            .map((item) => {
                if (!item.label_id) delete item['label_id'];
                return item;
            });
        handleAddAlternates(data);
    };

    const filteredAlternates = useMemo<VariantUpdateModel[]>(
        () => alternates.filter((item) => !item.deleted),
        [alternates],
    );

    const isAddButtonDisabled = useMemo<boolean>(
        () => !filteredAlternates.every((item) => item.text),
        [filteredAlternates],
    );

    return (
        <BasicModal
            header="Add image alternates"
            headerStyle={{
                background: aampeTheme.colors.white,
                textAlign: 'center',
                color: aampeTheme.colors.gray600,
                fontSize: aampeTheme.size.font.heading,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: aampeTheme.spacing4(2),
            }}
            width="800px"
        >
            <ModalBody>
                <AlternatesBody id={ImageAlternateModalId}>
                    <ImageAlternateBody
                        data={filteredAlternates}
                        formulaId={formulaId}
                        setAlternates={setAlternates}
                        isReadOnly={isLoading || !isEditable}
                        showPreview={!isLoading}
                        handleDeleteMaster={handleDeleteMaster}
                        handleAddOne={() =>
                            setAlternates((prev) => {
                                const data = [...prev];
                                data.push(
                                    getBlankAlternate(
                                        `ALTERNATE_${prev.length + 1}`,
                                        false,
                                    ),
                                );
                                return data;
                            })
                        }
                        showDeleteConfirmation={showDeleteConfirmation}
                        setShowDeleteConfirmation={setShowDeleteConfirmation}
                    />
                </AlternatesBody>
                <ActionContainer>
                    <BasicButton
                        isPrimary={false}
                        name={'Cancel'}
                        isOutlined
                        action={handleClose}
                    />
                    <BasicButton
                        name={isExisting ? 'Save' : 'Add alternates'}
                        action={handleAdd}
                        isDisabled={
                            isAddButtonDisabled || isLoading || !isEditable
                        }
                    />
                </ActionContainer>
            </ModalBody>
        </BasicModal>
    );
};

const ModalBody = styled.div({
    fontWeight: 'normal',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    rowGap: aampeTheme.spacing(2),
});

const AlternatesBody = styled.div({
    display: 'flex',
    flexDirection: 'column',
    rowGap: aampeTheme.spacing(2),
    minHeight: 300,
    maxHeight: 500,
    overflowY: 'auto',
});

const ActionContainer = styled.div({
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: '100%',
    columnGap: aampeTheme.spacing(2),
});

export default ImageAlternateModal;
